import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { TableCell, TableRow, Grid } from '@mui/material';
import { ThinCheckbox } from 'components/common/Styles';
import { useTranslation } from 'react-i18next';
import { ADMIN_NON_WORKING_DAYS_WRITE } from 'constants/rightsCodes';
import { usePermission } from 'providers/RightsHooks';
import FlexBox from 'components/common/FlexBox';
import { useMutation } from 'hooks/apollo/useMutation';
import { DELETE_NON_WORKING_DAY } from '../mutations';
import { useToast } from 'hooks/toasts/useToast';
import useConfirmDialog from 'features/common/components/confirm/useConfirmDialog';
import { nonWorkingDaysModalVar } from '../../variables';
import { emptyString } from 'utils/constants';
import useQuery from 'hooks/apollo/useQuery';
import { NON_WORKING_DAY_CONTEXTS_QUERY, SITES_QUERY } from '../queries';
import { Autocomplete } from '@totalsoft/rocket-ui';
import moment from 'moment';
import IconButton from 'components/common/buttons/IconButton';

const NonWorkingDayComponent = ({ data }) => {
  const canWrite = usePermission(ADMIN_NON_WORKING_DAYS_WRITE);
  const { t } = useTranslation();
  const addToast = useToast();
  const confirm = useConfirmDialog();

  const { data: dictionaryContext } = useQuery(NON_WORKING_DAY_CONTEXTS_QUERY);
  const { data: sites } = useQuery(SITES_QUERY);

  const allSites = useMemo(() => {
    const additionalSite = { siteId: -1, siteName: t('NonWorkingDays.AllSites') };
    return [...(sites?.sites || []), additionalSite];
  }, [sites, t]);

  const getSiteName = (workingSiteId, allSites) => {
    if (workingSiteId === -1) {
      return allSites.find(site => site.siteId === -1).siteName;
    } else {
      const site = allSites.find(site => site.siteId === workingSiteId);
      return site ? site.siteName : emptyString;
    }
  };
  const siteName = getSiteName(data.workingSiteId, allSites);

  const selectedContexts = useMemo(() => {
    if (!dictionaryContext || !dictionaryContext.nonWorkingDayContexts) return [];
    return dictionaryContext.nonWorkingDayContexts.filter(context => {
      const contexts = data.contexts;
      return Array.isArray(contexts) && contexts.includes(context.dictionaryItemId);
    });
  }, [data.contexts, dictionaryContext]);

  const [deleteNonWorkingDay] = useMutation(DELETE_NON_WORKING_DAY, {
    onError: error => addToast(error, 'error')
  });

  const handleDelete = useCallback(() => {
    confirm(
      () => {
        deleteNonWorkingDay({ variables: { id: data.nonWorkingDayId } });
      },
      {
        content: t('NonWorkingDays.AreYouSureDelete')
      }
    );
  }, [confirm, t, deleteNonWorkingDay, data.nonWorkingDayId]);

  return (
    <TableRow hover key={data.nonWorkingDayId}>
      <TableCell>{moment(data.nonWorkingDayDate).format('DD.MM.YYYY')}</TableCell>
      <TableCell>{`${data.description || emptyString}`}</TableCell>
      <TableCell>{`${siteName || emptyString}`}</TableCell>
      <TableCell align='center'>
        <ThinCheckbox checked={data.workingDay || false} disabled />
      </TableCell>
      <TableCell align='center'>
        <ThinCheckbox checked={data.validInAllContexts || false} disabled />
      </TableCell>
      <TableCell>
        <Grid item xs={12} xl={4}>
          <Autocomplete
            fullWidth
            value={selectedContexts}
            valueKey='dictionaryItemId'
            labelKey='dictionaryItemName'
            isMultiSelection={true}
            options={dictionaryContext}
            disabled
          />
        </Grid>
      </TableCell>
      <TableCell>
        <FlexBox gap={0.5} alignItems='center' justifyContent='end'>
          <IconButton
            type={canWrite ? 'edit' : 'view'}
            onClick={() =>
              nonWorkingDaysModalVar({
                open: true,
                item: {
                  nonWorkingDayId: data.nonWorkingDayId,
                  nonWorkingDayDate: data.nonWorkingDayDate,
                  description: data.description,
                  workingSiteId: data.workingSiteId,
                  workingDay: data.workingDay,
                  validInAllContexts: data.validInAllContexts,
                  contexts: data.contexts
                }
              })
            }
          />
          {canWrite && <IconButton type='delete' onClick={handleDelete} />}
        </FlexBox>
      </TableCell>
    </TableRow>
  );
};

NonWorkingDayComponent.propTypes = {
  data: PropTypes.object
};

export default NonWorkingDayComponent;
